import { useTranslation } from 'react-i18next';

import type { SignupTerm } from '../types/signup.types';

// US-SU-05 — dynamic Terms consent section. Replaces the old static SignupTermsLinks.
// Controlled: the parent owns `agreedIds`; this component renders checkboxes, the
// [약관 전체 동의] (Agree-to-All) row, Required/Optional badges, and the [보기] (View)
// link. [보기] opens the shared public terms viewer (/terms?typeId=…, FEATURE_041) in a
// new tab — the same UI the login/signup footer links use — instead of an inline modal.
// Agree-to-All is *derived* (checked ⇔ every term is selected), so it auto-syncs both
// ways without separate state.
interface SignupTermsConsentProps {
  terms: SignupTerm[];
  loading: boolean;
  error: boolean;
  agreedIds: number[];
  onChange: (next: number[]) => void;
  disabled?: boolean;
}

export const SignupTermsConsent = ({
  terms,
  loading,
  error,
  agreedIds,
  onChange,
  disabled = false,
}: SignupTermsConsentProps) => {
  const { t } = useTranslation('auth');

  const agreed = new Set(agreedIds);
  const allChecked = terms.length > 0 && terms.every((term) => agreed.has(term.termVersionId));

  const toggleOne = (termVersionId: number) => {
    const next = new Set(agreed);
    if (next.has(termVersionId)) next.delete(termVersionId);
    else next.add(termVersionId);
    onChange([...next]);
  };

  const toggleAll = () => {
    onChange(allChecked ? [] : terms.map((term) => term.termVersionId));
  };

  if (loading) {
    return (
      <div className="signup-page__terms">
        <p className="signup-page__terms-text">{t('signup.terms.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="signup-page__terms">
        <p className="signup-page__terms-text" role="alert">
          {t('signup.terms.loadError')}
        </p>
      </div>
    );
  }

  if (terms.length === 0) {
    return (
      <div className="signup-page__terms">
        <p className="signup-page__terms-text">{t('signup.terms.empty')}</p>
      </div>
    );
  }

  return (
    <div className="signup-page__terms">
      <label className="signup-page__terms-all">
        <input
          type="checkbox"
          checked={allChecked}
          onChange={toggleAll}
          disabled={disabled}
        />
        <span>{t('signup.terms.agreeAll')}</span>
      </label>

      <ul className="signup-page__terms-list">
        {terms.map((term) => (
          <li key={term.termVersionId} className="signup-page__terms-item">
            <label className="signup-page__terms-item-label">
              <input
                type="checkbox"
                checked={agreed.has(term.termVersionId)}
                onChange={() => toggleOne(term.termVersionId)}
                disabled={disabled}
              />
              <span
                className={
                  term.required
                    ? 'signup-page__terms-badge signup-page__terms-badge--required'
                    : 'signup-page__terms-badge'
                }
              >
                {term.required ? t('signup.terms.required') : t('signup.terms.optional')}
              </span>
              <span className="signup-page__terms-name">{term.name}</span>
            </label>
            <a
              className="signup-page__terms-view"
              href={`/terms?typeId=${term.typeId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('signup.terms.view')}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
};
